/**
 * Plan the reconciliation of multiple simultaneously-active stamp documents.
 *
 * The newest document (by start_timestamp) stays active. Every older document
 * is closed with the start_timestamp of the document that superseded it — NOT
 * the observer's wall clock. This keeps end times correct when an offline
 * device replays its queued stamps and another online device observes the
 * resulting churn: both writers derive the same end value from the data, so
 * concurrent closes are idempotent and cannot clobber each other.
 *
 * @param {Object} activeDocuments - Map of document id -> document data, as
 *   collected from the `active == true` snapshot.
 * @returns {{newest: {id: string, data: Object}, closures: Array<{id: string, endTimestamp: Date}>}}
 */
export function planActiveDocumentClosures(activeDocuments) {
  // Sort by start_timestamp, most recent first (missing timestamps sort oldest)
  const docArray = Object.entries(activeDocuments)
    .map(([id, data]) => ({
      id,
      data,
      timestamp: data.start_timestamp?.toDate() || new Date(0),
    }))
    .sort((a, b) => b.timestamp - a.timestamp);

  const newest = { id: docArray[0].id, data: docArray[0].data };

  // Each older document ends when the next-newer one started
  const closures = docArray.slice(1).map((doc, i) => ({
    id: doc.id,
    endTimestamp: docArray[i].timestamp,
  }));

  return { newest, closures };
}
